import { useState, useEffect, useCallback } from 'react';
import { customerApiService } from '../services/api/customerService';

export interface Notification {
    id: string;
    type: 'system' | 'kyc_approved' | 'kyc_rejected' | 'photo_approved' | 'photo_rejected' | 'support_ticket' | 'support_response' | 'credit_limit_updated' | 'status_changed' | 'wallet_funded' | 'wallet_debited' | 'transaction_success' | 'transaction_failed' | 'transaction_pending' | 'other';
    title: string;
    message: string;
    isRead: boolean;
    priority: 'low' | 'medium' | 'high' | 'urgent';
    data?: any;
    createdAt: string;
}

export const useNotifications = (pollInterval = 30000) => {
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchNotifications = useCallback(async () => {
        try {
            const response = await customerApiService.getNotifications({ limit: 20 });
            if (response.success && response.data) {
                setNotifications(response.data.notifications);
                setUnreadCount(response.data.unreadCount);
            }
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchNotifications();
        if (pollInterval > 0) {
            const interval = setInterval(fetchNotifications, pollInterval);
            return () => clearInterval(interval);
        }
        return undefined;
    }, [fetchNotifications, pollInterval]);

    const markAsRead = async (id: string) => {
        try {
            await customerApiService.markAsRead(id);
            setNotifications(prev =>
                prev.map(n => n.id === id ? { ...n, isRead: true } : n)
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    };

    const markAllAsRead = async () => {
        try {
            await customerApiService.markAllAsRead();
            setNotifications(prev =>
                prev.map(n => ({ ...n, isRead: true }))
            );
            setUnreadCount(0);
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
        }
    };

    const deleteNotification = async (id: string) => {
        try {
            const notification = notifications.find(n => n.id === id);
            await customerApiService.deleteNotification(id);
            setNotifications(prev => prev.filter(n => n.id !== id));
            if (notification && !notification.isRead) {
                setUnreadCount(prev => Math.max(0, prev - 1));
            }
        } catch (err) {
            console.error('Error deleting notification:', err);
        }
    };

    return {
        notifications,
        unreadCount,
        loading,
        error,
        refresh: fetchNotifications,
        markAsRead,
        markAllAsRead,
        deleteNotification
    };
};
